import { useEffect, useRef, useState } from "react";

// Poll for a new deploy and surface a flag when this tab runs old code. Used by
// <UpdateBanner /> to nudge users to reload after a fresh deploy before
// they hit a stale-chunk crash.

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

// Commit this bundle was built from (deploy_main.sh bakes it in).  Comparing
// the server's commit against it catches a tab that booted from an old cached
// index.html: the old "first version seen" baseline was already the new
// server version there, so no banner ever showed (ENQ-00386, Fan 2026-10-13).
const BUNDLE_COMMIT = (process.env.REACT_APP_BUILD_COMMIT || "").trim();

//
// The bundle commit is compared against /build_commit.txt, which
// deploy_frontend.sh writes next to the bundle it serves. It must NOT be
// compared against the backend's /api/health commit: a backend-only deploy
// (no frontend change, so no rebuild) moves that commit while the served
// bundle stays the same, and the banner then never goes away, even after
// "Reload now" (Fan 2026-10-13, after #658).
export function isStale(health, bundleCommit, baseline, servedCommit) {
  if (bundleCommit && servedCommit) return servedCommit !== bundleCommit;
  if (!health) return false;
  return baseline !== null && !!health.version && health.version !== baseline;
}

async function fetchServedCommit() {
  try {
    const res = await fetch("/build_commit.txt", { cache: "no-store" });
    if (!res.ok) return null;
    const text = (await res.text()).trim();
    // SPA fallback returns index.html for a missing file: only accept a hash.
    return /^[0-9a-f]{7,40}$/i.test(text) ? text : null;
  } catch {
    return null;
  }
}

async function fetchHealth() {
  try {
    const res = await fetch("/api/health", { cache: "no-store" });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

export function useVersionCheck(enabled = true) {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const baselineRef = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;

    const tick = async () => {
      const [health, served] = await Promise.all([
        fetchHealth(),
        BUNDLE_COMMIT ? fetchServedCommit() : Promise.resolve(null),
      ]);
      if (cancelled || (!health && !served)) return;
      if (isStale(health, BUNDLE_COMMIT, baselineRef.current, served)) {
        setUpdateAvailable(true);
        return;
      }
      if (baselineRef.current === null && health?.version) {
        // First successful response: record the version we booted with.
        baselineRef.current = health.version;
      }
    };

    tick();
    const interval = setInterval(tick, POLL_INTERVAL_MS);
    // A tab left open overnight should check as soon as it is looked at again.
    const onVisible = () => { if (document.visibilityState === "visible") tick(); };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [enabled]);

  return updateAvailable;
}
